import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadcalcLookup, loadcalcCompute } from "@/lib/dashboardApi";
import { toast } from "sonner";
import { Loader2, MapPin, Calculator, Wind, Flame, Gauge, Rocket } from "lucide-react";

const NUM = [
  ["ceiling_height", "Ceiling Height (ft)", "8"],
  ["occupants", "Occupants", "4"],
  ["summer_indoor", "Summer Indoor (°F)", "75"],
  ["summer_outdoor", "Summer Outdoor (°F)", "92"],
  ["winter_indoor", "Winter Indoor (°F)", "72"],
  ["winter_outdoor", "Winter Outdoor (°F)", "42"],
  ["grains_moisture_delta", "Moisture Δ (grains)", "45"],
  ["shr", "Sensible Heat Ratio", "0.75"],
  ["ceiling_r", "Ceiling R-value", ""],
  ["wall_r", "Wall R-value", ""],
  ["window_area", "Window Area (sq ft)", "auto"],
  ["window_u", "Window U-factor", ""],
  ["window_shgc", "Window SHGC", ""],
  ["duct_gain_pct", "Duct Gain (%)", "15"],
  ["duct_loss_pct", "Duct Loss (%)", "15"],
  ["cooling_infiltration_ach", "Cooling Infiltration (ACH)", "0.5"],
  ["heating_infiltration_ach", "Heating Infiltration (ACH)", "0.5"],
];
const SYSTEMS = ["Heat Pump", "AC + Gas Furnace", "AC + Air Handler", "Mini-Split", "Package Unit"];

export default function LoadCalc() {
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [f, setF] = useState({ system_type: "Heat Pump" });
  const [looking, setLooking] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const autofill = async () => {
    if (!address.trim()) { toast.error("Enter an address first"); return; }
    setLooking(true);
    try {
      const r = await loadcalcLookup(address.trim());
      if (r.available) {
        setF((p) => ({ ...p, conditioned_sqft: r.conditioned_sqft ?? p.conditioned_sqft, year_built: r.year_built ?? p.year_built }));
        toast.success(`Pulled from ${r.source || "property records"}: ${r.conditioned_sqft || "?"} sq ft, built ${r.year_built || "?"}`);
      } else {
        toast.error(r.reason || "No property data — enter manually");
      }
    } catch (e) { toast.error("Lookup failed — enter manually"); }
    finally { setLooking(false); }
  };

  const run = async () => {
    if (!f.conditioned_sqft) { toast.error("Conditioned Sq Ft is required"); return; }
    setRunning(true);
    try { setResult(await loadcalcCompute(f)); toast.success("Load calculated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Calculation failed"); }
    finally { setRunning(false); }
  };

  return (
    <div className="max-w-5xl" data-testid="loadcalc-page">
      <h1 className="font-heading text-2xl font-bold text-navy flex items-center gap-2"><Calculator className="w-6 h-6 text-launch" /> Load Calculation</h1>
      <p className="text-slate-500 text-sm mb-5">Whole-house Manual-J estimate. Auto-fill sq ft &amp; year built from the property record, or enter manually.</p>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <label className="text-[11px] text-slate-500">Property Address (Street, City, State, Zip)</label>
            <input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="loadcalc-address"
              placeholder="1600 N Atlantic Ave, Cocoa Beach, FL, 32931"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-0.5" />
          </div>
          <button onClick={autofill} disabled={looking} data-testid="loadcalc-autofill"
            className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy/90 text-white font-bold text-sm px-4 py-2 rounded-lg self-end disabled:opacity-60">
            {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />} Auto-fill from Property Appraiser
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="block">
            <span className="text-[11px] font-bold text-launch">Conditioned Sq Ft *</span>
            <input value={f.conditioned_sqft || ""} onChange={(e) => set("conditioned_sqft", e.target.value)} data-testid="loadcalc-sqft"
              className="w-full border-2 border-launch/40 rounded-lg px-3 py-2 text-sm mt-0.5" />
          </label>
          <label className="block">
            <span className="text-[11px] text-slate-500">Year Built</span>
            <input value={f.year_built || ""} onChange={(e) => set("year_built", e.target.value)} data-testid="loadcalc-year"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-0.5" />
          </label>
          {NUM.map(([k, label, ph]) => (
            <label key={k} className="block">
              <span className="text-[11px] text-slate-500">{label}</span>
              <input value={f[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} data-testid={`loadcalc-${k}`}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-0.5" />
            </label>
          ))}
          <label className="block">
            <span className="text-[11px] text-slate-500">System Type</span>
            <select value={f.system_type} onChange={(e) => set("system_type", e.target.value)} data-testid="loadcalc-system"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-0.5">
              {SYSTEMS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
        </div>

        <button onClick={run} disabled={running} data-testid="loadcalc-run"
          className="inline-flex items-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold px-6 py-2.5 rounded-lg disabled:opacity-60">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Run Calculation
        </button>
      </div>

      {result && (
        <div className="grid sm:grid-cols-3 gap-4 mt-5" data-testid="loadcalc-result">
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-5">
            <p className="flex items-center gap-2 text-sky-700 font-bold"><Wind className="h-4 w-4" /> Cooling Load</p>
            <p className="text-3xl font-heading font-extrabold text-navy mt-2">{result.cooling.total_btu.toLocaleString()} <span className="text-base font-semibold text-slate-400">BTU/hr</span></p>
            <p className="text-xs text-slate-500 mt-1">Sensible {result.cooling.sensible_btu.toLocaleString()} · Latent {result.cooling.latent_btu.toLocaleString()} · {result.cooling.tons} tons</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
            <p className="flex items-center gap-2 text-orange-700 font-bold"><Flame className="h-4 w-4" /> Heating Load</p>
            <p className="text-3xl font-heading font-extrabold text-navy mt-2">{result.heating.total_btu.toLocaleString()} <span className="text-base font-semibold text-slate-400">BTU/hr</span></p>
            <p className="text-xs text-slate-500 mt-1">System: {result.heating.system_type}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
            <p className="flex items-center gap-2 text-emerald-700 font-bold"><Gauge className="h-4 w-4" /> Recommendation</p>
            <p className="text-3xl font-heading font-extrabold text-navy mt-2">{result.recommendation.suggested_tonnage} <span className="text-base font-semibold text-slate-400">tons</span></p>
            <p className="text-xs text-slate-500 mt-1">{result.recommendation.system_type}</p>
          </div>
          <p className="sm:col-span-3 text-xs text-slate-400">{result.recommendation.note}{result.inputs_used.inferred_defaults ? " Envelope defaults inferred from year built." : ""}</p>
          <div className="sm:col-span-3">
            <button onClick={() => {
              sessionStorage.setItem("sellingComfortLoad", JSON.stringify({
                tonnage: result.recommendation.suggested_tonnage,
                system_type: result.recommendation.system_type,
                address,
              }));
              navigate("/admin/selling-comfort");
            }} data-testid="loadcalc-to-comfort"
              className="inline-flex items-center gap-2 bg-navy hover:bg-navy/90 text-white font-bold px-6 py-2.5 rounded-lg">
              <Rocket className="h-4 w-4" /> Build Good/Better/Best Options ({result.recommendation.suggested_tonnage} tons)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
